import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ElementRef,
  HostListener,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  of,
  catchError,
} from 'rxjs';
import {
  UserService,
  PlayerSearchResult,
} from '../../../shared/services/user.service';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { IconType } from '../../../shared/services/icon.service';
import { ToastService } from '../../../shared/services/toast.service';

export interface SelectedPlayer {
  id: string;
  name: string;
  email: string;
}

/**
 * Guest-allowance context for member-only bookings: the fee and remaining
 * units of the config the backend would select for the organizer. When set,
 * guest slots render even for booker-pays categories (pricingPerPlayer off)
 * and the guest hint shows the fee instead of "Standardpreis".
 */
export interface GuestAllowanceHint {
  /** Flat guest fee per hour in EUR */
  guestFeePerHour: number;
  /** Remaining guest units in the evaluated calendar year */
  remaining: number;
  /** Venue-local calendar year the units apply to */
  year: number;
  /** Date the quota resets (Jan 1, venue-local), YYYY-MM-DD */
  resetDate: string;
}

@Component({
  selector: 'app-player-selector',
  standalone: true,
  imports: [FormsModule, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="player-selector">
      <!-- Search input (always shown when players can be added) -->
      @if (canAddMore()) {
        <div class="search-container">
          <div class="search-input-wrapper">
            <app-icon
              [type]="IconType.SEARCH"
              [size]="18"
              class="search-icon"
            ></app-icon>
            <input
              type="text"
              [(ngModel)]="searchTerm"
              (ngModelChange)="onSearchTermChange($event)"
              (focus)="onInputFocus()"
              placeholder="Spieler suchen..."
              class="search-input"
              [attr.aria-label]="'Spieler suchen'"
              [attr.aria-expanded]="showDropdown"
              aria-haspopup="listbox"
              autocomplete="off"
            />
            @if (isSearching) {
              <span class="loading-spinner"></span>
            }
          </div>

          <!-- Dropdown results -->
          @if (
            showDropdown && (searchResults.length > 0 || noResultsMessage)
          ) {
            <div class="search-dropdown" role="listbox">
              @if (noResultsMessage) {
                <div class="no-results">
                  {{ noResultsMessage }}
                </div>
              }
              @for (result of searchResults; track result.id) {
                <div
                  class="dropdown-item"
                  role="option"
                  [attr.aria-selected]="false"
                  (click)="selectPlayer(result)"
                  (keydown.enter)="selectPlayer(result)"
                  tabindex="0"
                >
                  <div class="player-info">
                    <span class="player-name">{{ result.name }}</span>
                    <span class="player-email">{{ result.email }}</span>
                  </div>
                  <app-icon
                    [type]="IconType.PLUS"
                    [size]="16"
                    class="add-icon"
                  ></app-icon>
                </div>
              }
            </div>
          }
        </div>
      } @else {
        <!-- Max players reached message -->
        <div class="max-reached">Maximale Spieleranzahl erreicht</div>
      }

      <div class="player-slots-section">
        <h3>Benötigte Spieler ( {{ selectedPlayers.length + 1 + getGuestSlotIndices().length }} / {{ minPlayers }} )</h3>
        <div class="player-slots-list">
          <!-- Organizer slot (always first, non-removable) -->
          <div class="player-slot organizer-slot">
            <div class="slot-info">
              <app-icon
                [type]="IconType.USERS"
                [size]="18"
                class="slot-icon"
              ></app-icon>
              <span class="slot-name">{{ organizerName }}</span>
            </div>
            <span class="slot-badge organizer-badge">Organisator</span>
          </div>

          <!-- Selected players -->
          @for (player of selectedPlayers; track player.id) {
            <div class="player-slot selected-slot">
              <div class="slot-info">
                <app-icon
                  [type]="IconType.USERS"
                  [size]="18"
                  class="slot-icon"
                ></app-icon>
                <span class="slot-name">{{ player.name }}</span>
              </div>
              <button
                type="button"
                class="slot-remove-btn"
                (click)="removePlayer(player)"
                aria-label="Spieler entfernen"
              >
                <app-icon [type]="IconType.X" [size]="16"></app-icon>
              </button>
            </div>
          }

          <!-- Guest slots (per-player pricing mode OR guest allowance) -->
          @if (slotsVisible) {
            @for (i of getGuestSlotIndices(); track i) {
              <div class="player-slot guest-slot">
                <div class="slot-info">
                  <app-icon
                    [type]="IconType.USERS"
                    [size]="18"
                    class="slot-icon guest-icon"
                  ></app-icon>
                  <span class="slot-name guest-name">Gast</span>
                </div>
                @if (isOptionalGuestSlot(i)) {
                  <button
                    type="button"
                    class="slot-remove-btn"
                    (click)="removeGuestSlot()"
                    aria-label="Gast entfernen"
                  >
                    <app-icon [type]="IconType.X" [size]="16"></app-icon>
                  </button>
                } @else {
                  <span class="slot-hint">{{ guestSlotHint }}</span>
                }
              </div>
            }

            <!-- Add optional guest slot (between minPlayers and maxPlayers) -->
            @if (canAddGuestSlot()) {
              <button type="button" class="player-slot add-guest-slot guest-slot-placeholder" (click)="addGuestSlot()">
                <div class="slot-info">
                  <app-icon
                    [type]="IconType.PLUS"
                    [size]="18"
                    class="slot-icon guest-icon"
                  ></app-icon>
                  <span class="slot-name guest-name">Gast hinzufügen</span>
                </div>
                <span class="slot-hint">{{ guestSlotHint }}</span>
              </button>
            } @else if (isAllowanceExhausted()) {
              <div
                class="allowance-exhausted-hint"
                [title]="'Zurücksetzung am ' + (guestAllowance?.resetDate || '')"
              >
                Keine Gasteinheiten mehr verfügbar – Zurücksetzung am
                {{ guestAllowance?.resetDate }}
              </div>
            }
          }
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .player-selector {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      /* ============================================================================
       Player Slots Visualization
       ============================================================================ */

      .player-slots-section {
        margin-bottom: 0.25rem;
      }

      .player-slots-section h3 {
        margin: 0 0 0.75rem;
        font-size: 0.9375rem;
        font-weight: 600;
        color: #374151;
      }

      .player-slots-list {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .player-slot {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75rem;
        border-radius: 0.5rem;
        border: 1px solid #e5e7eb;
        background: white;
      }

      .player-slot .slot-info {
        display: flex;
        align-items: center;
        gap: 0.625rem;
      }

      .player-slot .slot-icon {
        color: var(--primary-green);
      }

      .player-slot .slot-name {
        font-size: 0.9375rem;
        font-weight: 500;
        color: #374151;
      }

      .player-slot .slot-badge {
        font-size: 0.6875rem;
        padding: 0.25rem 0.5rem;
        border-radius: 9999px;
        text-transform: uppercase;
        letter-spacing: 0.025em;
        font-weight: 500;
      }

      .player-slot .slot-badge.organizer-badge {
        background: var(--green-light-bg);
        color: var(--primary-green);
      }

      .player-slot.organizer-slot {
        background: #f8fafc;
        border-color: var(--secondary-green);
      }

      .player-slot.selected-slot {
        background: white;
      }

      .player-slot .slot-remove-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        padding: 0;
        border: none;
        background: #f3f4f6;
        color: #6b7280;
        border-radius: 50%;
        cursor: pointer;
        transition: all 0.15s;
      }

      .player-slot .slot-remove-btn:hover {
        background: #fecaca;
        color: #dc2626;
      }

      .player-slot.guest-slot-placeholder,
      .player-slot.add-guest-slot {
        background: #fafafa;
        border-style: dashed;
        border-color: #d1d5db;
      }

      .player-slot.add-guest-slot {
        cursor: pointer;
        transition: background 0.15s, border-color 0.15s;
        font: inherit;
        text-align: left;
        width: 100%;
      }

      .player-slot.add-guest-slot:hover {
        background: #f3f4f6;
        border-color: #9ca3af;
      }

      .player-slot.guest-slot-placeholder .guest-icon {
        color: #9ca3af;
      }

      .player-slot.guest-slot-placeholder .guest-name {
        color: #9ca3af;
        font-style: italic;
      }

      .player-slot .slot-hint {
        font-size: 0.75rem;
        color: #9ca3af;
      }

      .allowance-exhausted-hint {
        padding: 0.5rem 0.75rem;
        font-size: 0.8125rem;
        color: #b45309;
        background: #fffbeb;
        border: 1px dashed #fcd34d;
        border-radius: 0.5rem;
      }

      /* ============================================================================
       Player Count & Search
       ============================================================================ */

      .player-count {
        font-size: 0.875rem;
        color: #6b7280;
      }

      .player-count .warning {
        color: #dc2626;
        font-weight: 500;
      }

      .search-container {
        position: relative;
      }

      .search-input-wrapper {
        position: relative;
        display: flex;
        align-items: center;
      }

      .search-icon {
        position: absolute;
        left: 0.75rem;
        color: #9ca3af;
        pointer-events: none;
      }

      .search-input {
        width: 100%;
        height: 44px;
        padding: 0 0.75rem 0 2.5rem;
        border: 1px solid #d1d5db;
        border-radius: 0.375rem;
        font-size: 0.875rem;
        font-family: inherit;
        color: #374151;
        background: white;
        transition:
          border-color 0.2s,
          box-shadow 0.2s;
      }

      .search-input:focus {
        outline: none;
        border-color: var(--primary-color);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      .search-input::placeholder {
        color: #9ca3af;
      }

      .loading-spinner {
        position: absolute;
        right: 0.75rem;
        width: 18px;
        height: 18px;
        border: 2px solid #e5e7eb;
        border-top-color: var(--primary-color);
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      .search-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        margin-top: 4px;
        background: white;
        border: 1px solid #d1d5db;
        border-radius: 0.375rem;
        box-shadow:
          0 4px 6px -1px rgba(0, 0, 0, 0.1),
          0 2px 4px -1px rgba(0, 0, 0, 0.06);
        z-index: 50;
        max-height: 200px;
        overflow-y: auto;
      }

      .dropdown-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75rem;
        cursor: pointer;
        transition: background-color 0.15s;
      }

      .dropdown-item:hover,
      .dropdown-item:focus {
        background-color: #f3f4f6;
        outline: none;
      }

      .player-info {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
      }

      .player-info .player-name {
        font-size: 0.875rem;
        font-weight: 500;
        color: #374151;
      }

      .player-info .player-email {
        font-size: 0.75rem;
        color: #6b7280;
      }

      .add-icon {
        color: var(--primary-color);
      }

      .no-results {
        padding: 0.75rem;
        text-align: center;
        color: #6b7280;
        font-size: 0.875rem;
      }

      .max-reached {
        padding: 0.5rem;
        text-align: center;
        color: #6b7280;
        font-size: 0.875rem;
        background-color: #f3f4f6;
        border-radius: 0.375rem;
      }
    `,
  ],
})
export class PlayerSelectorComponent implements OnInit, OnDestroy {
  @Input() organizationId: string = '';
  @Input() minPlayers: number = 1;
  @Input() maxPlayers: number = 4;
  @Input() disabled: boolean = false;
  @Input() showSlots: boolean = false;
  /**
   * Guest-allowance context (member-only bookings). When set, guest slots
   * render even when showSlots is false (booker-pays categories) and the
   * guest hint shows the fee and remaining units.
   */
  @Input() guestAllowance: GuestAllowanceHint | null = null;
  @Input() organizerName: string = 'Du';
  @Input() set initialPlayers(players: SelectedPlayer[]) {
    if (players) {
      this.selectedPlayers = [...players];
      this.cdr.markForCheck();
    }
  }
  @Output() playersChanged = new EventEmitter<SelectedPlayer[]>();
  @Output() guestSlotCountChanged = new EventEmitter<number>();

  readonly IconType = IconType;

  searchTerm: string = '';
  searchResults: PlayerSearchResult[] = [];
  selectedPlayers: SelectedPlayer[] = [];
  showDropdown: boolean = false;
  isSearching: boolean = false;
  noResultsMessage: string = '';
  extraGuestSlots: number = 0;

  private searchSubject = new Subject<string>();
  private subscriptions = new Subscription();
  private initTimerId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private userService: UserService,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    // Emit initial guest slot count (deferred to avoid ExpressionChangedAfterItHasBeenCheckedError)
    this.initTimerId = setTimeout(() => this.emitGuestSlotCount());

    // Setup debounced search
    this.subscriptions.add(
      this.searchSubject
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((term) => {
            if (!term || term.length < 2 || !this.organizationId) {
              this.searchResults = [];
              this.noResultsMessage =
                term && term.length < 2 ? 'Mindestens 2 Zeichen eingeben' : '';
              this.isSearching = false;
              this.cdr.markForCheck();
              return of([]);
            }

            this.isSearching = true;
            this.cdr.markForCheck();

            return this.userService
              .searchPlayers(this.organizationId, term)
              .pipe(
                catchError(() => {
                  this.noResultsMessage = 'Fehler bei der Suche';
                  this.toastService.error('Spielersuche fehlgeschlagen. Bitte erneut versuchen.');
                  return of([]);
                }),
              );
          }),
        )
        .subscribe((results) => {
          this.isSearching = false;

          // Filter out already selected players
          const selectedIds = new Set(this.selectedPlayers.map((p) => p.id));
          this.searchResults = results.filter((r) => !selectedIds.has(r.id));

          if (
            this.searchTerm.length >= 2 &&
            this.searchResults.length === 0 &&
            !this.noResultsMessage
          ) {
            this.noResultsMessage = 'Keine Spieler gefunden';
          } else if (this.searchResults.length > 0) {
            this.noResultsMessage = '';
          }

          this.cdr.markForCheck();
        }),
    );
  }

  ngOnDestroy(): void {
    if (this.initTimerId) clearTimeout(this.initTimerId);
    this.subscriptions.unsubscribe();
    this.searchSubject.complete();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.showDropdown = false;
      this.cdr.markForCheck();
    }
  }

  onSearchTermChange(term: string): void {
    this.searchSubject.next(term);
    this.showDropdown = true;
  }

  onInputFocus(): void {
    if (this.searchTerm.length >= 2) {
      this.showDropdown = true;
    }
  }

  selectPlayer(player: PlayerSearchResult): void {
    if (!this.canAddMore()) {
      return;
    }

    const selectedPlayer: SelectedPlayer = {
      id: player.id,
      name: player.name,
      email: player.email,
    };

    this.selectedPlayers = [...this.selectedPlayers, selectedPlayer];
    this.searchTerm = '';
    this.searchResults = [];
    this.showDropdown = false;
    this.noResultsMessage = '';

    // Registered player fills an optional guest slot if any
    if (this.extraGuestSlots > 0) {
      this.extraGuestSlots--;
    }

    this.playersChanged.emit(this.selectedPlayers);
    this.emitGuestSlotCount();
    this.cdr.markForCheck();
  }

  removePlayer(player: SelectedPlayer): void {
    this.selectedPlayers = this.selectedPlayers.filter(
      (p) => p.id !== player.id,
    );
    this.playersChanged.emit(this.selectedPlayers);
    this.emitGuestSlotCount();
    this.cdr.markForCheck();
  }

  canAddMore(): boolean {
    // maxPlayers includes the organizer, so available slots for additional players is maxPlayers - 1
    return this.selectedPlayers.length < this.maxPlayers - 1 && !this.disabled;
  }

  /** Guest slots render in per-player mode OR when a guest allowance applies. */
  get slotsVisible(): boolean {
    return this.showSlots || this.guestAllowance !== null;
  }

  /** Hint shown on guest rows: fee + remaining units, or the default rate. */
  get guestSlotHint(): string {
    if (this.guestAllowance) {
      const fee = this.guestAllowance.guestFeePerHour.toFixed(2).replace('.', ',');
      const units = this.guestAllowance.remaining;
      // Singular matters here: `remaining === 1` is the LAST-unit case, the one
      // a member reads most carefully before deciding to bring someone.
      // House idiom for plurals in this repo is an inline ternary at the call
      // site (there is no i18n pipe) — see guest-allowance-list.component.html
      // and shared/utils/format-lead-time.ts.
      const unitWord = units === 1 ? 'Gasteinheit' : 'Gasteinheiten';
      return `Gastgebühr: ${fee} €/Std. · noch ${units} ${unitWord} in ${this.guestAllowance.year}`;
    }
    return 'Standardpreis';
  }

  getGuestSlotIndices(): number[] {
    if (!this.slotsVisible) {
      return [];
    }
    const filledSlots = 1 + this.selectedPlayers.length;
    const requiredGuests = Math.max(0, this.minPlayers - filledSlots);
    const totalGuests = Math.min(
      requiredGuests + this.extraGuestSlots,
      this.maxPlayers - filledSlots,
    );
    return Array.from({ length: Math.max(0, totalGuests) }, (_, i) => i);
  }

  canAddGuestSlot(): boolean {
    if (!this.slotsVisible) {
      return false;
    }
    const filledSlots = 1 + this.selectedPlayers.length;
    const currentGuests = this.getGuestSlotIndices().length;
    if (filledSlots + currentGuests >= this.maxPlayers) {
      return false;
    }
    // Guest allowance: no further guest slots once the remaining units are
    // exhausted (the exhausted hint below the list carries the reset date).
    if (this.guestAllowance && currentGuests >= this.guestAllowance.remaining) {
      return false;
    }
    return true;
  }

  /** True when adding a guest is blocked purely by the exhausted allowance. */
  isAllowanceExhausted(): boolean {
    if (!this.guestAllowance) {
      return false;
    }
    const filledSlots = 1 + this.selectedPlayers.length;
    const currentGuests = this.getGuestSlotIndices().length;
    return (
      filledSlots + currentGuests < this.maxPlayers &&
      currentGuests >= this.guestAllowance.remaining
    );
  }

  addGuestSlot(): void {
    this.extraGuestSlots++;
    this.emitGuestSlotCount();
    this.cdr.markForCheck();
  }

  isOptionalGuestSlot(index: number): boolean {
    const filledSlots = 1 + this.selectedPlayers.length;
    const requiredGuests = Math.max(0, this.minPlayers - filledSlots);
    return index >= requiredGuests;
  }

  removeGuestSlot(): void {
    if (this.extraGuestSlots > 0) {
      this.extraGuestSlots--;
      this.emitGuestSlotCount();
      this.cdr.markForCheck();
    }
  }

  private emitGuestSlotCount(): void {
    this.guestSlotCountChanged.emit(this.getGuestSlotIndices().length);
  }

}
